import * as React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "../../../store/";
import { Outlet } from "react-router-dom";
import { Container, CssBaseline, Box } from "@mui/material";
import Logo from "./../LogoText";
import { getLayout } from "../../../utils";
import qs from "qs";
import { DEFAULT_QUIZ } from "../../../constants";

export default function MainLayout() {
	const location = useLocation();
	const navigate = useNavigate();
	const learner = useSelector((state) => state.learner || {});
	const client = useSelector((state) => state.client || {});
	const publicUrl = process.env.PUBLIC_URL;
	const headerSM = location.pathname === "/instructions";
	let layout = getLayout(learner);
	const [webLayoutStyle, setWebLayoutStyle] = React.useState({});

	React.useEffect(() => {
		let { quizId } = qs.parse(window.location.search, {
			ignoreQueryPrefix: true,
		});
		// set default to acsf3
		quizId = quizId ?? DEFAULT_QUIZ;
		// check here if there is test level via learner
		if (typeof learner?.details?.testLevel !== "undefined") {
			quizId = learner?.details?.testLevel;
		}

		if (
			learner?.details?.complete === true &&
			location.pathname !== `${publicUrl}/complete`
		) {
			navigate(`${publicUrl}/complete${location?.search || ""}`);
		}

		if (
			learner?.details?.complete === false &&
			location.pathname === `${publicUrl}/complete`
		) {
			navigate(`${publicUrl}/${location?.search || ""}`);
		}

		// for disabled quiz
		if (client?.details?.quizDisabled === true &&
			location.pathname !== `${publicUrl}/disabled`
		) {
			navigate(`${publicUrl}/disabled${location?.search || ""}`);
		}

		// if disabled the current path we will check if the quizDisabled is false
		// redirect to main url
		if (client?.details?.quizDisabled === false &&
			location.pathname === `${publicUrl}/disabled`
		) {
			navigate(`${publicUrl}/${location?.search || ""}`);
		}


		// for quiz flags per quiz type
		if (typeof client?.details?.quizFlags !== "undefined" &&
			location.pathname !== `${publicUrl}/disabledquiztype` &&
			location.pathname !== `${publicUrl}/complete` &&
			location.pathname !== `${publicUrl}/disabled`
		) {
			// checking here flag is false
			if (!client?.details?.quizFlags[quizId]) {
				navigate(`${publicUrl}/disabledquiztype${location?.search || ""}`);
			}
		}

		// if disabled quiz type the current path we will check if the current quiz is enabled
		// redirect to main url
		if (typeof client?.details?.quizFlags !== "undefined" &&
			location.pathname === `${publicUrl}/disabledquiztype`
		) {
			// checking here flag is true
			if (client?.details?.quizFlags[quizId]) {
				navigate(`${publicUrl}/${location?.search || ""}`);
			}
		}


		// console.log(location.pathname, `${publicUrl}/resume`);
		if (
			(location.pathname !== `${publicUrl}/resume` ||
				learner?.showResumePage === false) &&
			(location.pathname !== `${publicUrl}/continue` ||
				learner?.showModuleBreak === false) &&
			learner?.details?.complete !== true &&
			(learner?.details?.answers || []).length > 0 &&
			location.pathname !== `${publicUrl}/disabledquiztype` &&
			location.pathname !== `${publicUrl}/disabled`
		) {
			navigate(`${publicUrl}/question${location?.search || ""}`);
		}
	}, [learner, location, navigate, publicUrl, client]);


	// this is for web layout use
	const handleChangeWebStyleLayout = React.useCallback(({style}) => {
		setWebLayoutStyle(style);
	}, []);

	return (
		<React.Fragment>
			{layout === "mobile" 
			?
			<React.Fragment>
				<CssBaseline />
				<Container
					maxWidth="sm"
					sx={{
						bgcolor: "#06b6ad",
						height: "100%",
						display: "flex",
						flexDirection: "column",
						p: { xs: 0, sm: "2px" },
						pl: { xs: 0, sm: "6px" },
						position: "relative",
					}}
				>
					<Box
						sx={{
							minHeight: "125px",
							height: {
								xs: `calc(100% - ${headerSM ? "90vh" : "70vh"})`,
								sm: `calc(100% - ${headerSM ? "90vh" : "70vh"})`,
							},
							py: headerSM ? 1 : 10,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						{client?.details?.logo?.absolute ? (
							<img
								alt="logo"
								src={client.details.logo.absolute}
								style={{
									maxWidth: headerSM ? "10rem" : "22rem",
									maxHeight: headerSM ? "7rem" : "11rem",
								}}
							/>
						) : (
							<Logo width="80px" />
						)}
					</Box>
					<Outlet context={[handleChangeWebStyleLayout, layout]}/>
				</Container>
			</React.Fragment>
			: <Box sx={{
				position: "relative",
				width: "100%",
				height: "100%",
				backgroundColor: "#2C9DA3",
				// for small desktop
				"@media (max-width: 1024px), (max-height: 768px)": {
					transform: "scale(0.8)",
					transformOrigin: "left top",
					width: "125%",
					height: "125%",
					overflowY: "none",
				},
				// for tablet
				"@media (max-width: 800px), (max-height: 600px)": {
					transform: "scale(0.7)",
					transformOrigin: "left top",
					width: "142.85%",
					height: "142.85%",
					overflowY: "none",
				},
				fontFamily: "'Arimo', sans-serif",
			}}>
				<Box sx={{
					boxSizing: "border-box",
					width: "100%",
					height: "100%",
					padding: "30px",
					backgroundColor: webLayoutStyle.backgroundColor,
					borderBottom: webLayoutStyle.borderBottom,
					textAlign: "center",
				}}>
					<Box sx={{
						boxSizing: "border-box",
						backgroundColor: "white",
						width: "100%",
						height: "100%",
						borderTop: webLayoutStyle.borderBottom,
						padding: "10px",
						display: "flex",
						flexDirection: "column",
						justifyContent: webLayoutStyle.justifyContent ?? "space-between",
						alignItems: webLayoutStyle.alignItems ?? "center",
						textAlign: "center",
					}}>
						{/* For Body */}
						<Outlet context={[handleChangeWebStyleLayout, layout]}/>

						{/* For Footer */}
						<Box hidden={webLayoutStyle.hideFooter} sx={{
							position: "absolute",
							width: "100%",
							height: "60px",
							left: "0px",
							right: "0px",
							bottom: "0px",
							textAlign: "center",
							color: "white",
						}}>
							<Box sx={{
								textAlign: "center",
								padding: "18px",
								fontWeight: "bold",
								fontSize: "1.25em",
							}}>{client?.details?.displayName} | {client?.details?.preferences?.learnerCorrespondence?.phone} | {client?.details?.preferences?.learnerCorrespondence?.email}</Box>
						</Box>
					</Box>
				</Box>
			</Box>
			}
		</React.Fragment>
	);
}
